import { useEffect, useState } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { supabase } from '@/integrations/supabase/client';
import { usePromotedListingsV2 } from '@/hooks/useListingsV2';
import { completeHomeRecommendations, HOME_ROTATION_MS, homeRotationSlot } from '@/utils/homeRecommendations';

export const homeRecommendationPoolQueryOptions = (country: string) =>
  queryOptions({
    queryKey: ['home-recommendation-pool', country],
    queryFn: async () => {
      // Public display fields only; fillers do not acquire promotion status.
      const { data, error } = await supabase.from('listings_v2')
        .select('id,make,model,year,price,currency,mileage_value,location_city,location_region,photos,created_at,status,country_code,user_id,seller_type,dealer_verified,categories:category_id!inner(code)')
        .eq('status', 'active').eq('country_code', country)
        .in('categories.code', ['cars', 'suv'])
        .order('created_at', { ascending: false }).limit(200);
      if (error) throw error;
      return (data || []).map((row: any) => ({ ...row, category_code: row.categories.code }));
    },
    staleTime: HOME_ROTATION_MS,
  });

export function useHomeRecommendations(country: string) {
  // The homepage loader fixes the rotation slot so SSR and hydration pick the same cars.
  const loaderSlot = useRouterState({
    select: (s) => (s.matches.find((m) => m.routeId === '/')?.loaderData as { homeSlot?: number } | undefined)?.homeSlot,
  });
  const [slot, setSlot] = useState<number | null>(loaderSlot ?? null);
  useEffect(() => {
    setSlot(homeRotationSlot());
    const timer = window.setInterval(() => setSlot(homeRotationSlot()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const priority = usePromotedListingsV2(6, country);
  const pool = useQuery({
    ...homeRecommendationPoolQueryOptions(country),
    enabled: slot !== null && !priority.isPending && (priority.data?.length || 0) < 6,
  });
  return {
    data: completeHomeRecommendations(priority.data || [], pool.data || [], country, slot ?? 0),
    isLoading: priority.isLoading || (slot !== null && pool.isLoading),
  };
}
